/**
 * LessonExtraModals — houses two modals surfaced from the LessonViewer's
 * right-hand "Lesson tools" rail:
 *
 *   1. Study Together modal   — spin up (or join) a peer study room
 *      with a one-click shareable link. Uses the existing
 *      `POST /api/live-sessions` (session_type=study_room) endpoint.
 *
 *   2. Requirements modal     — surfaces the lesson's prerequisites
 *      (prior lessons in the same course), required equipment
 *      (course tier/themes/skill tags), and access-code status pulled
 *      from the parent course record so learners know what they need
 *      before diving in.
 *
 * Kept as a single component so LessonViewer only imports one thing;
 * each modal is fully self-contained and can be toggled independently.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Video, Users, Copy, Loader2, Sparkles, CheckCircle2, Circle, Lock,
  Wrench, KeyRound, GraduationCap, ExternalLink,
} from 'lucide-react';

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────────────────────
// Study Together
// ─────────────────────────────────────────────────────────────
const StudyTogetherModal = ({ open, onClose, lessonId, lessonTitle, courseId }) => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchRooms = useCallback(async () => {
    if (!lessonId || !open) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lms/lessons/${lessonId}/study-context`);
      setRooms(data?.active_study_rooms || []);
    } catch { /* silent — modal still shows the create CTA */ }
    finally { setLoading(false); }
  }, [API, lessonId, open]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const createRoom = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/live-sessions`, {
        title:            `Study: ${(lessonTitle || 'Lesson').slice(0, 60)}`,
        description:      `Peer study room for "${lessonTitle || 'this lesson'}".`,
        scheduled_start:  new Date().toISOString(),
        duration_minutes: 60,
        max_attendees:    8,
        session_type:     'study_room',
        lesson_id:        lessonId,
        course_id:        courseId || null,
        features:         { whiteboard: true, breakouts: true, chat: true, screenshare: true },
      });
      setCreatedRoom(data);
      toast.success('Study room live — share the link with your peers');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create study room');
    } finally { setCreating(false); }
  };

  const shareUrl = useMemo(() => {
    if (!createdRoom?.id) return '';
    return `${window.location.origin}/live/${createdRoom.id}/room`;
  }, [createdRoom]);

  const doCopy = async () => {
    if (!shareUrl) return;
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error('Copy failed — long-press to select the link.');
    }
  };

  const joinRoom = (id) => {
    onClose();
    navigate(`/live/${id}/room`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="study-together-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold inline-flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Study together
          </DialogTitle>
          <DialogDescription>
            Grab a live whiteboard-first room for this lesson and share the link with a study buddy.
          </DialogDescription>
        </DialogHeader>

        {createdRoom ? (
          <div className="space-y-3">
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
                Room is live
              </div>
              <div className="font-heading font-extrabold text-sm mb-3">{createdRoom.title}</div>
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} data-testid="study-together-share-url" />
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={doCopy}
                  data-testid="study-together-copy-btn"
                >
                  {copied ? <><CheckCircle2 className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                </Button>
              </div>
            </div>
            <Button
              onClick={() => joinRoom(createdRoom.id)}
              className="w-full rounded-full font-bold"
              data-testid="study-together-enter-btn"
            >
              <Video className="w-4 h-4 mr-2" /> Enter the room
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {loading ? (
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading rooms…
              </div>
            ) : rooms.length > 0 ? (
              <>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  {rooms.length} room{rooms.length === 1 ? '' : 's'} open right now
                </div>
                <ul className="space-y-1.5" data-testid="study-together-room-list">
                  {rooms.slice(0, 5).map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => joinRoom(r.id)}
                        className="w-full flex items-center gap-2 rounded-lg border border-border bg-card hover:bg-muted/50 px-3 py-2 text-left"
                        data-testid={`study-together-join-${r.id}`}
                      >
                        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{r.title}</div>
                          <div className="text-[10px] text-muted-foreground">
                            Hosted by {r.host_name?.split('@')[0] || 'peer'} · {r.live_attendee_count || 0} in room
                          </div>
                        </div>
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white flex-shrink-0">
                          Join
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                No one is studying this lesson yet — start a room and invite a peer.
              </div>
            )}
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-[11px] text-primary flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <strong>One-click room:</strong> we spin up a whiteboard, breakout rooms, and chat,
                then hand you a shareable link.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full font-bold"
            data-testid="study-together-close"
          >
            Close
          </Button>
          {!createdRoom && (
            <Button
              onClick={createRoom}
              disabled={creating || !user}
              className="rounded-full font-bold"
              data-testid="study-together-create-btn"
            >
              {creating
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Starting…</>
                : <><Video className="w-3.5 h-3.5 mr-1.5" /> Start a new room</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// Requirements
// ─────────────────────────────────────────────────────────────
const RequirementsModal = ({ open, onClose, lesson }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!open || !lesson?.id) return undefined;
    (async () => {
      setLoading(true);
      try {
        const { data: payload } = await axios.get(
          `${API}/lms/lessons/${lesson.id}/requirements`,
        );
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) {
          setData({
            prerequisites: [],
            equipment: [],
            access_code_required: !!lesson?.access_code_required,
            has_access: true,
            tier: lesson?.tier || null,
            course_title: null,
            error: err?.response?.data?.detail || 'Could not load lesson requirements',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, open, lesson?.id, lesson?.access_code_required, lesson?.tier]);

  const goToLesson = (id, prereqTitle) => {
    // Remember where the learner came from so the LessonViewer for the
    // prereq can surface a "Return to <original>" banner and auto-jump
    // back once the prereq is marked complete.
    if (lesson?.id && id && id !== lesson.id) {
      try {
        localStorage.setItem(
          'lms:requirements-return',
          JSON.stringify({
            return_to_lesson_id: lesson.id,
            return_to_lesson_title: lesson.title || '',
            prereq_lesson_id: id,
            prereq_lesson_title: prereqTitle || '',
            saved_at: Date.now(),
          }),
        );
      } catch { /* localStorage may be unavailable — non-fatal */ }
    }
    onClose();
    navigate(`/lesson/${id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="requirements-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold inline-flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-primary" /> Lesson requirements
          </DialogTitle>
          <DialogDescription>
            What you'll need before diving into <strong>{lesson?.title || 'this lesson'}</strong>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            {data?.error && (
              <div className="rounded-lg border border-rose-400/40 bg-rose-500/5 p-3 text-xs text-rose-600 dark:text-rose-300">
                {data.error}
              </div>
            )}

            {/* Access */}
            <section data-testid="requirements-access">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" /> Access
              </div>
              {data?.access_code_required ? (
                data?.has_access ? (
                  <div className="text-sm text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Access code required — you're in.
                  </div>
                ) : (
                  <div className="text-sm text-rose-700 dark:text-rose-300 inline-flex items-center gap-1.5">
                    <Lock className="w-4 h-4" /> Access code required — redeem one to enrol.
                  </div>
                )
              ) : (
                <div className="text-sm text-foreground/85">
                  Open to any enrolled learner{data?.tier ? ` · tier: ${String(data.tier).replace('tier_', 'Tier ')}` : ''}.
                </div>
              )}
            </section>

            {/* Prerequisites */}
            <section data-testid="requirements-prereqs">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                <GraduationCap className="w-3.5 h-3.5" /> Prerequisites
              </div>
              {data?.prerequisites?.length > 0 ? (
                <ul className="space-y-1">
                  {data.prerequisites.map((p) => (
                    <li key={p.id} className="flex items-start gap-2">
                      {p.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      )}
                      <button
                        type="button"
                        onClick={() => goToLesson(p.id, p.title)}
                        className="text-left text-sm hover:text-primary hover:underline underline-offset-2 flex-1"
                        data-testid={`requirements-prereq-${p.id}`}
                      >
                        {p.title}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {p.completed ? 'done' : 'pending'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-foreground/70">
                  None — you can start this lesson without completing anything else first.
                </div>
              )}
            </section>

            {/* Equipment */}
            <section data-testid="requirements-equipment">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1 inline-flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5" /> Equipment & skills
              </div>
              {data?.equipment?.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {data.equipment.map((e, i) => (
                    <li
                      key={`${e}-${i}`}
                      className="text-xs px-2 py-1 rounded-full border border-border bg-muted/40 text-foreground/85"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-foreground/70">
                  No specific equipment listed. A working freeze dryer or shelf-stable notes are usually enough.
                </div>
              )}
            </section>

            {data?.course_title && data?.course_id && (
              <button
                type="button"
                onClick={() => { onClose(); navigate(`/course/${data.course_id}`); }}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid="requirements-course-link"
              >
                <ExternalLink className="w-3 h-3" /> Open parent course: {data.course_title}
              </button>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full font-bold"
            data-testid="requirements-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────
// Bundle export — one component for LessonViewer to mount.
// ─────────────────────────────────────────────────────────────
const LessonExtraModals = ({
  studyOpen, onCloseStudy,
  requirementsOpen, onCloseRequirements,
  lessonId, lessonTitle, courseId, lesson,
}) => (
  <>
    <StudyTogetherModal
      open={studyOpen}
      onClose={onCloseStudy}
      lessonId={lessonId}
      lessonTitle={lessonTitle}
      courseId={courseId}
    />
    <RequirementsModal
      open={requirementsOpen}
      onClose={onCloseRequirements}
      lesson={lesson}
    />
  </>
);

export default LessonExtraModals;
